import apiClient from './client';
import type { AuditEvent } from '../types';

export interface AdminModel {
  id: string;
  name: string;
  provider: string;
  model_name: string;
  base_url?: string;
  embeddings_model?: string;
  embeddings_base_url?: string;
  is_active: boolean;
  deprecated?: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminPrompt {
  id: string;
  name: string;
  task_type: string;
  content: string;
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateModelRequest {
  name: string;
  provider: string;
  model_name: string;
  base_url?: string;
  embeddings_model?: string;
  embeddings_base_url?: string;
  embeddings_api_key?: string;
  is_active?: boolean;
  deprecated?: boolean;
  api_key?: string;
}

export interface UpdateApiKeyRequest {
  api_key: string;
}

export interface CreatePromptRequest {
  name: string;
  task_type: string;
  content: string;
  is_active?: boolean;
}

export interface AdminSetting {
  id: string;
  key: string;
  value?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateSettingRequest {
  key: string;
  value?: string;
  description?: string;
}

export const adminApi = {
  audit: {
    getAll: async (params?: {
      limit?: number;
      before?: string;
      after?: string;
      q?: string;
      entity_type?: string;
      entity_id?: string;
      action?: string;
      actor_type?: string;
      actor_email?: string;
      actor_user_id?: string;
      job_id?: string;
      series_id?: string;
      series_title?: string;
      chapter_id?: string;
    }): Promise<AuditEvent[]> => {
      const response = await apiClient.get<AuditEvent[]>('/audit', { params });
      return response.data;
    },
  },
  models: {
    getAll: async (): Promise<AdminModel[]> => {
      const response = await apiClient.get<AdminModel[]>('/admin/models');
      return response.data;
    },
    getByName: async (name: string): Promise<AdminModel> => {
      const response = await apiClient.get<AdminModel>(`/admin/models/${name}`);
      return response.data;
    },
    createOrUpdate: async (data: CreateModelRequest): Promise<AdminModel> => {
      const response = await apiClient.post<AdminModel>('/admin/models', data);
      return response.data;
    },
    updateApiKey: async (name: string, apiKey: string): Promise<void> => {
      await apiClient.post(`/admin/models/${name}/api-key`, { api_key: apiKey });
    },
    delete: async (name: string): Promise<{ success: boolean; deleted: { id: string; name: string } }> => {
      const response = await apiClient.delete<{ success: boolean; deleted: { id: string; name: string } }>(`/admin/models/${name}`);
      return response.data;
    },
  },
  prompts: {
    getAll: async (): Promise<AdminPrompt[]> => {
      const response = await apiClient.get<AdminPrompt[]>('/admin/prompts');
      return response.data;
    },
    getByName: async (name: string): Promise<AdminPrompt> => {
      const response = await apiClient.get<AdminPrompt>(`/admin/prompts/${name}`);
      return response.data;
    },
    createOrUpdate: async (data: CreatePromptRequest): Promise<AdminPrompt> => {
      const response = await apiClient.post<AdminPrompt>('/admin/prompts', data);
      return response.data;
    },
  },
  settings: {
    getAll: async (): Promise<AdminSetting[]> => {
      const response = await apiClient.get<AdminSetting[]>('/admin/settings');
      return response.data;
    },
    getByKey: async (key: string): Promise<AdminSetting> => {
      const response = await apiClient.get<AdminSetting>(`/admin/settings/${key}`);
      return response.data;
    },
    createOrUpdate: async (data: CreateSettingRequest): Promise<AdminSetting> => {
      const response = await apiClient.post<AdminSetting>('/admin/settings', data);
      return response.data;
    },
  },
  cache: {
    clearOcr: async (): Promise<{ message: string; deletedCount: number }> => {
      const response = await apiClient.delete<{ message: string; deletedCount: number }>('/admin/cache/ocr');
      return response.data;
    },
    getOcrStats: async (): Promise<{ total_entries: string; unique_hashes: string; oldest_entry: string | null; newest_entry: string | null }> => {
      const response = await apiClient.get<{ total_entries: string; unique_hashes: string; oldest_entry: string | null; newest_entry: string | null }>('/admin/cache/ocr/stats');
      return response.data;
    },
  },
  providers: {
    getApiKey: async (provider: 'openai' | 'gemini' | 'anthropic'): Promise<{ provider: string; has_key: boolean }> => {
      const response = await apiClient.get<{ provider: string; has_key: boolean }>(`/admin/providers/${provider}/api-key`);
      return response.data;
    },
    updateApiKey: async (provider: 'openai' | 'gemini' | 'anthropic', apiKey: string): Promise<void> => {
      await apiClient.post(`/admin/providers/${provider}/api-key`, { api_key: apiKey });
    },
    verifyApiKey: async (provider: 'openai' | 'gemini' | 'anthropic'): Promise<{ provider: string; valid: boolean; error?: string | null }> => {
      const response = await apiClient.get<{ provider: string; valid: boolean; error?: string | null }>(`/admin/providers/${provider}/api-key/verify`);
      return response.data;
    },
  },
};
